import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';
import StoreLayout from '../../components/store/StoreLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { leadAPI, publicAPI } from '../../lib/api';
import { trackQuizComplete, trackLead } from '../../lib/tracking';
import LookCard from '../../components/store/LookCard';
import { toast } from 'sonner';

const QUESTIONS = [
  {
    id: 'style',
    question: 'quiz.question1',
    options: [
      { value: 'chic', label: 'Élégant & Raffiné', description: 'Des pièces intemporelles et sophistiquées' },
      { value: 'casual', label: 'Décontracté & Moderne', description: 'Confort et style au quotidien' },
      { value: 'glamour', label: 'Audacieux & Glamour', description: 'Des looks qui attirent les regards' },
    ]
  },
  {
    id: 'occasion',
    question: 'quiz.question2',
    options: [
      { value: 'work', label: 'Bureau & Réunions', description: 'Professionnalisme et élégance' },
      { value: 'weekend', label: 'Week-end & Loisirs', description: 'Sorties décontractées' },
      { value: 'evening', label: 'Soirées & Événements', description: 'Occasions spéciales' },
    ]
  },
  {
    id: 'budget',
    question: 'quiz.question3',
    options: [
      { value: 'standard', label: '200€ - 400€', description: 'Excellent rapport qualité-prix' },
      { value: 'premium', label: '400€ - 600€', description: 'Pièces de qualité supérieure' },
      { value: 'luxury', label: '600€+', description: 'L\'excellence sans compromis' },
    ]
  }
];

export default function QuizPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [showEmailStep, setShowEmailStep] = useState(false);

  const currentQuestion = QUESTIONS[step];
  const progress = ((step + 1) / QUESTIONS.length) * 100;

  const handleAnswer = (value) => {
    setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }));
    
    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      setShowEmailStep(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Save lead
      await leadAPI.create({
        email,
        source: 'QUIZ',
        consent_marketing: true,
        quiz_results: answers,
        tags: ['quiz_completed', `style_${answers.style}`, `occasion_${answers.occasion}`]
      });

      // Track events
      trackLead('quiz');
      trackQuizComplete(answers);

      // Fetch matching looks
      const response = await publicAPI.getLooks({
        style: answers.style,
        limit: 3
      });

      setResults(response.data.items);
      toast.success('Merci ! Voici vos looks personnalisés.');
    } catch (error) {
      console.error('Quiz error:', error);
      toast.error('Erreur lors de la soumission');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    if (showEmailStep) {
      setShowEmailStep(false);
    } else if (step > 0) {
      setStep(step - 1);
    }
  };

  // Results view
  if (results) {
    return (
      <StoreLayout>
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-12"
            >
              <Sparkles className="h-12 w-12 mx-auto text-accent mb-4" />
              <h1 className="font-display text-4xl sm:text-5xl font-bold">
                {t('quiz.results')}
              </h1>
              <p className="mt-4 text-muted-foreground text-lg max-w-2xl mx-auto">
                Basé sur vos préférences : style {answers.style}, pour {answers.occasion}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="quiz-results">
              {results.map((look, index) => (
                <LookCard key={look.id} look={look} index={index} />
              ))}
            </div>

            <div className="text-center mt-12">
              <Button size="lg" onClick={() => navigate('/looks')}>
                Voir tous les looks
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </section>
      </StoreLayout>
    );
  }

  return (
    <StoreLayout>
      <section className="min-h-[80vh] flex items-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto w-full">
          {/* Progress bar */}
          <div className="mb-8">
            <div className="h-1 bg-muted rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-foreground"
                initial={{ width: 0 }}
                animate={{ width: `${showEmailStep ? 100 : progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              {showEmailStep ? 'Dernière étape' : `Question ${step + 1} sur ${QUESTIONS.length}`}
            </p>
          </div>

          {/* Back button */}
          {(step > 0 || showEmailStep) && (
            <Button
              variant="ghost"
              className="mb-6"
              onClick={goBack}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('quiz.prev')}
            </Button>
          )}

          <AnimatePresence mode="wait">
            {showEmailStep ? (
              // Email step
              <motion.div
                key="email"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h2 className="font-display text-3xl sm:text-4xl font-bold mb-4">
                  Presque terminé !
                </h2>
                <p className="text-muted-foreground text-lg mb-8">
                  Entrez votre email pour découvrir vos looks personnalisés et recevoir nos meilleures sélections.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    type="email"
                    placeholder="votre@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="text-lg py-6"
                    data-testid="quiz-email-input"
                  />
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full"
                    disabled={loading}
                    data-testid="quiz-submit"
                  >
                    {loading ? 'Chargement...' : t('quiz.finish')}
                    <Sparkles className="ml-2 h-4 w-4" />
                  </Button>
                </form>

                <p className="text-xs text-muted-foreground mt-4 text-center">
                  En soumettant, vous acceptez de recevoir nos communications marketing.
                </p>
              </motion.div>
            ) : (
              // Question step
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h2 className="font-display text-3xl sm:text-4xl font-bold mb-8">
                  {t(currentQuestion.question)}
                </h2>

                <div className="space-y-4">
                  {currentQuestion.options.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleAnswer(option.value)}
                      className={`w-full p-6 text-left border-2 rounded-lg transition-all hover:border-foreground hover:bg-muted/50 ${
                        answers[currentQuestion.id] === option.value
                          ? 'border-foreground bg-muted'
                          : 'border-border'
                      }`}
                      data-testid={`quiz-option-${option.value}`}
                    >
                      <h3 className="font-semibold text-lg">{option.label}</h3>
                      <p className="text-muted-foreground mt-1">{option.description}</p>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </StoreLayout>
  );
}
